import api from './api';

// NPC (National Privacy Commission) Compliance Configuration
export const NPC_COMPLIANCE = {
  // NPC Registration details (to be filled after registration)
  registrationNumber: 'Registration in Progress',
  registrationDate: '-',
  expiryDate: '-',

  // Data Protection Officer details (Updated for Sole Proprietor Launch)
  dpo: {
    name: 'To be appointed', // Will be filled by user
    email: 'dpo@agosidentity.com',
    phone: '',
    address: 'Kidapawan City, Philippines',
  },

  // NPC contact for complaints
  npcContact: {
    name: 'National Privacy Commission',
    address: '5th Floor, Delegation Building, PICC Complex, Pasay City, Philippines',
    phone: '+63 (2) 8234-2228',
    email: 'info@privacy.gov.ph',
    website: 'https://privacy.gov.ph',
  },
};

export class PrivacyService {
  // Data retention periods in days - Updated for AMLA compliance
  // Data retention periods in days - Updated for AMLA and NPC compliance
  private readonly retentionPeriods = {
    verifications: 1825,    // 5 years for verification records (AMLA requirement)
    analytics: 365,         // 1 year for analytics (NPC guideline)
    logs: 365,              // 1 year for audit logs (NPC guideline)
    payment: 1825,          // 5 years for payment data (AMLA + BIR requirement)
    personalInfo: 1825,     // 5 years for personal information (AMLA)
    consent: 1825,          // 5 years for consent records
  };

  // Raw Sensitive Biometric Image Retention (Technical Measure)
  // These are purged rapidly for privacy, while the derived verification result is kept per AMLA.
  public readonly biometricImageRetentionHours = 24;

  // Get retention period for a specific data type
  getRetentionPeriod(dataType: keyof typeof this.retentionPeriods): number {
    return this.retentionPeriods[dataType];
  }

  // Get all retention periods (for transparency)
  getAllRetentionPeriods(): typeof this.retentionPeriods {
    return { ...this.retentionPeriods };
  }

  async requestDataExport(userId: string): Promise<any> {
    const response = await api.request(`/api/users/${userId}/data-export`, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to export data');
    }

    return this.formatDataExport(response.data);
  }

  async deleteUserData(userId: string): Promise<void> {
    // Check if user has pending verifications under AMLA retention
    const hasAMLARetention = await this.checkAMLARetention(userId);

    if (hasAMLARetention) {
      // Anonymize instead of delete for AMLA compliance
      await this.anonymizeData(userId);
    } else {
      await api.request(`/api/users/${userId}/data`, {
        method: 'DELETE',
      });
    }
  }

  // Check if user data is under AMLA retention requirement
  private async checkAMLARetention(userId: string): Promise<boolean> {
    const response = await api.request(`/api/users/${userId}/amla-retention-check`);
    return response.data?.hasRetention || false;
  }

  async updateDataConsent(userId: string, consents: {
    marketing: boolean;
    analytics: boolean;
    thirdParty: boolean;
    biometricProcessing?: boolean;  // Required for Philippine biometric data
    crossBorderTransfer?: boolean;  // For international data transfer
  }): Promise<void> {
    await api.request(`/api/users/${userId}/consents`, {
      method: 'PUT',
      body: JSON.stringify({
        marketing: consents.marketing,
        analytics: consents.analytics,
        thirdParty: consents.thirdParty,
        biometricProcessing: consents.biometricProcessing ?? false,
        crossBorderTransfer: consents.crossBorderTransfer ?? false,
        consentVersion: '2.0-PH', // Philippine-specific consent version
      }),
    });
  }

  async anonymizeData(userId: string): Promise<void> {
    await api.request(`/api/users/${userId}/anonymize`, {
      method: 'POST',
    });
  }

  // Record data breach (NPC requires notification within 72 hours)
  async recordDataBreach(breach: {
    description: string;
    affectedDataTypes: string[];
    affectedUserCount: number;
    discoveredAt: Date;
    severity: 'low' | 'medium' | 'high' | 'critical';
  }): Promise<{
    breachId: string;
    npcNotificationDeadline: Date;
  }> {
    const response = await api.request('/api/admin/data-breaches', {
      method: 'POST',
      body: JSON.stringify({
        description: breach.description,
        affectedDataTypes: breach.affectedDataTypes,
        affectedUserCount: breach.affectedUserCount,
        discoveredAt: breach.discoveredAt.toISOString(),
        severity: breach.severity,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to record data breach');
    }

    return {
      breachId: response.data.id,
      npcNotificationDeadline: new Date(response.data.npcNotificationDeadline),
    };
  }

  // Get user's consent status
  async getConsentStatus(userId: string): Promise<{
    hasConsented: boolean;
    consents: Record<string, boolean>;
    consentDate: string | null;
  }> {
    const response = await api.request(`/api/users/${userId}/consents`);

    if (!response.success || !response.data) {
      return {
        hasConsented: false,
        consents: {},
        consentDate: null,
      };
    }

    const data = response.data;
    return {
      hasConsented: true,
      consents: {
        marketing: data.marketing,
        analytics: data.analytics,
        thirdParty: data.thirdParty,
        biometricProcessing: data.biometricProcessing,
        crossBorderTransfer: data.crossBorderTransfer,
      },
      consentDate: data.updatedAt,
    };
  }

  private formatDataExport(data: any): any {
    // Format data according to NPC data portability requirements
    return {
      exportDate: new Date().toISOString(),
      dataController: {
        name: 'Agos Identity Verification',
        npcRegistration: NPC_COMPLIANCE.registrationNumber,
        dpo: NPC_COMPLIANCE.dpo,
      },
      personalInfo: {
        email: data?.email,
        companyName: data?.companyName,
        createdAt: data?.createdAt,
      },
      verifications: data?.verifications?.map((v: any) => ({
        id: v.id,
        type: v.documentType,
        status: v.status,
        date: v.createdAt,
        // Exclude actual document data for security
      })),
      payments: data?.payments?.map((p: any) => ({
        amount: p.amount,
        currency: p.currency,
        date: p.createdAt,
        status: p.status,
      })),
      consents: data?.consents ? {
        marketing: data.consents.marketing,
        analytics: data.consents.analytics,
        thirdParty: data.consents.thirdParty,
        biometricProcessing: data.consents.biometricProcessing,
        lastUpdated: data.consents.updatedAt,
      } : null,
      retentionPolicy: this.getAllRetentionPeriods(),
    };
  }
}

export const privacyService = new PrivacyService();
